/**
 * Crumb — Single breadcrumb item.
 *
 * When `isCurrent` is true, renders a non-clickable `<span>` with
 * `aria-current="page"` in primary colour. Otherwise renders an
 * `<a>` (when `href` is provided) or a plain `<span>`.
 *
 * Callers that need a full breadcrumb trail should use the
 * `<Breadcrumbs>` component instead.
 *
 * @see specs/integration-plan.md Section 5.4
 */
import React from "react";

export interface CrumbProps {
  /** Breadcrumb label text. */
  label: string;
  /** Navigation target. Ignored when `isCurrent` is true. */
  href?: string;
  /** Marks this crumb as the current page. */
  isCurrent?: boolean;
  /** Inline style override. */
  style?: React.CSSProperties;
  /** Forwarded className. */
  className?: string;
  /** Forwarded data-testid. */
  "data-testid"?: string;
  /** Any additional HTML attributes. */
  [key: string]: unknown;
}

const baseStyle: React.CSSProperties = {
  font: "var(--fs-mono)/1.4 var(--font-mono)",
  textTransform: "uppercase",
  letterSpacing: "0.28px",
  textDecoration: "none",
};

export function Crumb({
  label,
  href,
  isCurrent,
  style,
  ...rest
}: CrumbProps) {
  if (isCurrent) {
    return (
      <span
        aria-current="page"
        style={{
          ...baseStyle,
          color: "var(--color-action-blue)",
          ...style,
        }}
        {...rest}
      >
        {label}
      </span>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        style={{
          ...baseStyle,
          color: "var(--fg3)",
          ...style,
        }}
        {...rest}
      >
        {label}
      </a>
    );
  }

  return (
    <span
      style={{
        ...baseStyle,
        color: "var(--fg3)",
        ...style,
      }}
      {...rest}
    >
      {label}
    </span>
  );
}

export default Crumb;
